import { z } from 'zod'

/**
 * Runtime env — one place. All `VITE_*` values are validated at boot so a
 * typo in `.env.local` fails loud rather than silently rendering an empty
 * MSAL client id or an undefined API base URL two hours later.
 */
const schema = z.object({
  VITE_VISION_BASE_URL: z.string().url().default('https://vision.staging.boldfit.ai'),
  VITE_MSAL_CLIENT_ID: z.string().min(1).default('00000000-0000-0000-0000-000000000000'),
  VITE_MSAL_TENANT_ID: z.string().min(1).default('94ed1c5a-da93-48b1-9309-06c7a95abb2d'),
  VITE_MSAL_REDIRECT_URI: z.string().url().default('http://localhost:5173/'),
  VITE_VISION_API_SCOPE: z.string().min(1).default('api://vision-ui/access'),
  VITE_AUTH_PROVIDER: z.enum(['msal', 'shield']).default('msal'),
  VITE_SHIELD_URL: z.string().url().default('https://shield.boldfit.ai'),
  VITE_SHIELD_CLIENT_ID: z.string().min(1).default('pulse-web'),
  VITE_SHIELD_APPLICATION: z.string().min(1).default('pulse'),
  VITE_DEFAULT_TENANT: z.string().min(1).default('bbpl-main'),
  VITE_DEV_JWT_OVERRIDE: z.string().optional().default(''),
  VITE_TIME_ZONE: z.string().min(1).default('Asia/Kolkata'),
  VITE_LOCALE: z.string().min(1).default('en-IN'),
  // VineRetail parent shell (UAT). Inner screens like orderMaintenanceBS crash
  // as a top-level tab (`getParentHeight`). Prod deploy.yml uses boldfit.vineretail.com.
  VITE_VINCULUM_ORDER_URL_TEMPLATE: z
    .string()
    .min(1)
    .default('https://boldfituat.vineretail.com/eRetailWeb/'),
  // Business Central Sales Order deep link, with a `{no}` placeholder for the BC
  // `SalesOrder.No`. Empty by default and empty everywhere it is not configured:
  // the Fulfilment panel renders the SO number with no link rather than a link
  // into whichever BC company the template happened to name.
  VITE_BC_SALES_ORDER_URL_TEMPLATE: z.string().default(''),
  // Staging and prod deploy.yml set true so operators can Acknowledge and
  // Send-to-warehouse. Default false keeps local/CI bundles read-only.
  VITE_OMS_WRITES_ENABLED: z.enum(['true', 'false']).default('false'),
  // Rollout gate for canonical PO cancellation. Enable only after every Vision replica supports
  // manual_cancelled_quantity and the Vision-side gate is on.
  VITE_PO_CANCELLATION_ENABLED: z.enum(['true', 'false']).default('false'),
  // Local-only fixture adapter for reviewing the bulk allocation workspace without running
  // Vision, Heimdall, PostgreSQL, and the inventory refresh stack.
  VITE_BULK_UI_DEMO: z.enum(['true', 'false']).default('false'),
  // Local-only fixtures for the Sales Order journey screen. Vision's
  // `/v1/sales-orders` pair is being built in parallel with the screen, so
  // this serves `features/sales-orders/fixtures` instead of calling it.
  // Delete the flag once the endpoints deploy — without it the routes render
  // their "not available yet" state, which is the honest thing to show.
  VITE_SALES_ORDER_FIXTURES: z.enum(['true', 'false']).default('false'),
  // JIT procurement is rolling out behind an explicit surface flag. The demo
  // adapter is dev-only and lets product/ops review the complete interaction
  // before Vision and Vulcan expose their production contracts.
  VITE_JIT_UI_ENABLED: z.enum(['true', 'false']).default('false'),
  VITE_JIT_UI_DEMO: z.enum(['true', 'false']).default('false'),
})

const parsed = schema.safeParse(import.meta.env)
if (!parsed.success) {
  console.error('[env] invalid VITE_* configuration:', parsed.error.flatten().fieldErrors)
}

export const env = parsed.success ? parsed.data : schema.parse({})

export const IS_DEV = import.meta.env.DEV
export const HAS_DEV_JWT = IS_DEV && env.VITE_DEV_JWT_OVERRIDE.length > 0
export const USING_SHIELD_AUTH = env.VITE_AUTH_PROVIDER === 'shield'
export const OMS_WRITES_ENABLED = env.VITE_OMS_WRITES_ENABLED === 'true'
export const PO_CANCELLATION_ENABLED = env.VITE_PO_CANCELLATION_ENABLED === 'true'
export const BULK_UI_DEMO = IS_DEV && env.VITE_BULK_UI_DEMO === 'true'
export const SALES_ORDER_FIXTURES = IS_DEV && env.VITE_SALES_ORDER_FIXTURES === 'true'
export const JIT_UI_ENABLED = env.VITE_JIT_UI_ENABLED === 'true'
export const JIT_UI_DEMO = IS_DEV && JIT_UI_ENABLED && env.VITE_JIT_UI_DEMO === 'true'
